const MAX_APP_LOAD_PERFORMANCE_MARKS = 20;
const MAX_APP_LOAD_RESOURCE_ENTRIES = 12;
const REDACTED_PATH_SEGMENT = ':id';

function safelyDecodePathSegment(segment: string) {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function isLikelyIdentifierPathSegment(segment: string, previousSegment: string | undefined) {
  const decodedSegment = safelyDecodePathSegment(segment);
  const normalizedPreviousSegment = previousSegment?.toLowerCase();

  if (['people', 'persons', 'customers', 'profiles'].includes(normalizedPreviousSegment ?? '')) {
    return true;
  }

  return (
    /^\d+$/.test(segment) ||
    /^[\da-f]{16,}$/i.test(segment) ||
    /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(segment) ||
    /^[\w-]{24,}$/.test(segment) ||
    /@|%40/i.test(segment) ||
    /@/.test(decodedSegment)
  );
}

export function sanitizeAppLoadDiagnosticPath(path: string) {
  const pathWithoutSearch = path.split(/[#?]/)[0] ?? '';
  return pathWithoutSearch
    .split('/')
    .map((segment, index, segments) => {
      if (segment === '') {
        return segment;
      }

      return isLikelyIdentifierPathSegment(segment, segments[index - 1]) ? REDACTED_PATH_SEGMENT : segment;
    })
    .join('/')
    .slice(0, 300);
}

function sanitizeResourceName(name: string) {
  try {
    const url = new URL(name, window.location.origin);
    return `${url.origin}${sanitizeAppLoadDiagnosticPath(url.pathname)}`.slice(0, 300);
  } catch {
    return sanitizeAppLoadDiagnosticPath(name).slice(0, 300);
  }
}

function isEmbeddedAppResource(entry: PerformanceResourceTiming) {
  if (!['script', 'link', 'css'].includes(entry.initiatorType)) {
    return false;
  }

  return /(?:fly|unityassets)\.customer\.io|localhost:\d+|\.cio\.localhost|entrypoint\.js/.test(entry.name);
}

function getRecentEmbeddedAppResources() {
  try {
    return performance
      .getEntriesByType('resource')
      .filter((entry): entry is PerformanceResourceTiming => 'initiatorType' in entry)
      .filter(isEmbeddedAppResource)
      .slice(-MAX_APP_LOAD_RESOURCE_ENTRIES)
      .map((entry) => {
        const entryWithResponseStatus = entry as PerformanceResourceTiming & { responseStatus?: number };
        return {
          name: sanitizeResourceName(entry.name),
          initiatorType: entry.initiatorType,
          durationMs: Math.round(entry.duration),
          transferSize: entry.transferSize,
          encodedBodySize: entry.encodedBodySize,
          responseStatus: entryWithResponseStatus.responseStatus,
        };
      });
  } catch {
    return [];
  }
}

function getRecentAppLoadPerformanceMarks() {
  try {
    return performance
      .getEntriesByType('mark')
      .filter((entry) => entry.name.startsWith('hydra:'))
      .slice(-MAX_APP_LOAD_PERFORMANCE_MARKS)
      .map((entry) => ({
        name: entry.name,
        startTimeMs: Math.round(entry.startTime),
      }));
  } catch {
    return [];
  }
}

export function getEmbeddedAppLoadDiagnostics() {
  return {
    appLoadPerformanceMarks: getRecentAppLoadPerformanceMarks(),
    recentEmbeddedAppResources: getRecentEmbeddedAppResources(),
  };
}
